import type { BodyDetails } from '../physics/types';

/**
 * Per-body planetary detail data (geology, composition, internal layers,
 * temperatures, atmosphere). Sources: NASA Planetary Fact Sheet,
 * Lodders 2003 (solar composition), USGS Astrogeology, IPCC atmospheric
 * data. All user-visible strings are LangText (zh-Hant / en / ja).
 */

export const DETAILS: Record<string, BodyDetails> = {
  sun: {
    classification: { 'zh-Hant': 'G2V 主序星', 'en': 'G2V Main-sequence Star', 'ja': 'G2V 主系列星' },
    meanSurfaceTempC: 5505,
    surfaceTempRangeC: [4000, 6000],
    surfaceGravityMS2: 274,
    escapeVelocityKmS: 617.5,
    bulkComposition: [
      { name: { 'zh-Hant': '氫 (H)', 'en': 'Hydrogen (H)', 'ja': '水素 (H)' }, pct: 73.46 },
      { name: { 'zh-Hant': '氦 (He)', 'en': 'Helium (He)', 'ja': 'ヘリウム (He)' }, pct: 24.85 },
      { name: { 'zh-Hant': '氧 (O)', 'en': 'Oxygen (O)', 'ja': '酸素 (O)' }, pct: 0.77 },
      { name: { 'zh-Hant': '碳 (C)', 'en': 'Carbon (C)', 'ja': '炭素 (C)' }, pct: 0.29 },
      { name: { 'zh-Hant': '鐵 (Fe)', 'en': 'Iron (Fe)', 'ja': '鉄 (Fe)' }, pct: 0.16 },
      { name: { 'zh-Hant': '其他', 'en': 'Others', 'ja': 'その他' }, pct: 0.47 },
    ],
    internalStructure: [
      { layer: { 'zh-Hant': '核心', 'en': 'Core', 'ja': '中心核' },
        description: { 'zh-Hant': '半徑 0–25%；溫度 1500 萬 K，密度 150 g/cm³，氫核融合為氦發生於此。', 'en': 'Inner 25% radius; 15M K, 150 g/cm³. Hydrogen fuses into helium here.', 'ja': '半径 0–25%、1500 万 K、密度 150 g/cm³。水素核融合の場。' } },
      { layer: { 'zh-Hant': '輻射層', 'en': 'Radiative Zone', 'ja': '放射層' },
        description: { 'zh-Hant': '半徑 25–70%；能量以光子輻射形式向外傳遞，光子穿越需 17 萬年。', 'en': '25–70% radius; energy transferred outward as photons (taking ~170k years to escape).', 'ja': '半径 25–70%、光子で外へ熱輸送。光子の通過に 17 万年。' } },
      { layer: { 'zh-Hant': '對流層', 'en': 'Convection Zone', 'ja': '対流層' },
        description: { 'zh-Hant': '半徑 70–100%；電漿對流上升下沉形成米粒組織。', 'en': '70–100% radius; convective plasma cells form the granulation pattern.', 'ja': '半径 70–100%、対流が粒状斑を形成。' } },
      { layer: { 'zh-Hant': '光球層', 'en': 'Photosphere', 'ja': '光球' },
        description: { 'zh-Hant': '可見「表面」，厚 ~500 km，溫度 5505°C，光子最終逃逸進入太空。', 'en': 'Visible "surface", ~500 km thick, 5505°C; photons finally escape into space.', 'ja': '可視の「表面」、厚さ 500 km、5505°C。光子が宇宙へ放出される。' } },
      { layer: { 'zh-Hant': '色球層 / 日冕', 'en': 'Chromosphere / Corona', 'ja': '彩層 / コロナ' },
        description: { 'zh-Hant': '稀薄外層，日冕溫度反而升至 100 萬 K（高溫之謎尚未完全解釋）。', 'en': 'Thin outer layers; corona reaches 1M K (heating mechanism still debated).', 'ja': '希薄な外層。コロナは 100 万 K に達する（加熱機構は未解明）。' } },
    ],
    geology: { 'zh-Hant': '太陽質量佔太陽系 99.86%，每秒將 6 億噸氫融合成氦並損失 4 百萬噸質量，能量以光與微中子釋放。預期再 50 億年後膨脹為紅巨星。',
      'en': 'Holds 99.86% of solar-system mass. Fuses 600M tons of hydrogen per second, losing 4M tons as energy emitted as light and neutrinos. Will expand into a red giant in ~5B years.',
      'ja': '太陽系全質量の 99.86% を占める。毎秒 6 億トンの水素を融合し、400 万トンが光と微小中性子としてエネルギー放出。約 50 億年後に赤色巨星化。' },
    notableFacts: [
      { 'zh-Hant': '11 年磁活動週期，黑子數量、閃焰、日冕物質拋射隨之起伏。',
        'en': '11-year magnetic activity cycle drives sunspot count, flares, and CMEs.',
        'ja': '11 年周期の磁気活動。黒点数・フレア・CME がこれに連動。' },
      { 'zh-Hant': '太陽風以 400–800 km/s 向外吹拂，影響整個太陽圈直到太陽風頂。',
        'en': 'Solar wind blows outward at 400–800 km/s, shaping the heliosphere out to the heliopause.',
        'ja': '太陽風は 400–800 km/s で吹き出し、太陽圏を太陽風端まで形成。' },
    ],
  },

  mercury: {
    classification: { 'zh-Hant': '類地行星（最內、最小）', 'en': 'Terrestrial planet (innermost, smallest)', 'ja': '地球型惑星（最内・最小）' },
    meanSurfaceTempC: 167,
    surfaceTempRangeC: [-173, 427],
    surfaceGravityMS2: 3.7,
    escapeVelocityKmS: 4.25,
    bulkComposition: [
      { name: { 'zh-Hant': '鐵 (Fe，含核心)', 'en': 'Iron (Fe, incl. core)', 'ja': '鉄 (Fe、核を含む)' }, pct: 70 },
      { name: { 'zh-Hant': '矽酸鹽 (mantle/crust)', 'en': 'Silicates (mantle/crust)', 'ja': 'ケイ酸塩 (マントル・地殻)' }, pct: 30 },
    ],
    atmosphere: {
      pressureNote: { 'zh-Hant': '近乎真空（外逸層 ~ 10⁻¹⁵ kPa）', 'en': 'Essentially vacuum (exosphere ~10⁻¹⁵ kPa)', 'ja': 'ほぼ真空（外圏 ~10⁻¹⁵ kPa）' },
      composition: [
        { name: { 'zh-Hant': '氧 (O)', 'en': 'Oxygen (O)', 'ja': '酸素 (O)' }, pct: 42 },
        { name: { 'zh-Hant': '鈉 (Na)', 'en': 'Sodium (Na)', 'ja': 'ナトリウム (Na)' }, pct: 29 },
        { name: { 'zh-Hant': '氫 (H)', 'en': 'Hydrogen (H)', 'ja': '水素 (H)' }, pct: 22 },
        { name: { 'zh-Hant': '氦 (He)', 'en': 'Helium (He)', 'ja': 'ヘリウム (He)' }, pct: 6 },
        { name: { 'zh-Hant': '其他', 'en': 'Others', 'ja': 'その他' }, pct: 1 },
      ],
    },
    internalStructure: [
      { layer: { 'zh-Hant': '核心', 'en': 'Core', 'ja': '中心核' },
        description: { 'zh-Hant': '半徑約 1 800 km，佔全行星 ~75% 半徑；液態鐵-鎳，產生微弱磁場。', 'en': '~1,800 km radius, 75% of the planet; liquid iron-nickel; generates a weak magnetic field.', 'ja': '半径約 1,800 km、惑星の 75%。液体の鉄-ニッケルで微弱な磁場を生む。' } },
      { layer: { 'zh-Hant': '地函', 'en': 'Mantle', 'ja': 'マントル' },
        description: { 'zh-Hant': '厚約 600 km，矽酸鹽。', 'en': '~600 km thick, silicates.', 'ja': '厚さ約 600 km、ケイ酸塩。' } },
      { layer: { 'zh-Hant': '地殼', 'en': 'Crust', 'ja': '地殻' },
        description: { 'zh-Hant': '厚 100–300 km，富含碳質與硫化物。', 'en': '100–300 km thick, rich in carbonates and sulfides.', 'ja': '厚さ 100–300 km、炭素質と硫化物に富む。' } },
    ],
    geology: { 'zh-Hant': '表面類似月球：滿布隕石坑、平原（如 Caloris 盆地直徑 1550 km）、皺脊（lobate scarps）顯示行星冷卻收縮。極區永夜坑底有水冰。',
      'en': 'Moon-like surface: heavily cratered, plains (e.g. the 1,550 km Caloris Basin), lobate scarps showing planetary cooling/contraction. Polar permanently-shadowed crater floors hold water ice.',
      'ja': '月に似た表面：クレーターと平原（カロリス盆地・直径 1,550 km）、惑星冷却収縮を示すローベイトスカープ。極の永久影クレーターには水氷。' },
    notableFacts: [
      { 'zh-Hant': '一個太陽日 = 176 地球日（自轉 58.6 日 + 公轉 88 日的共振）。', 'en': 'One solar day = 176 Earth days (a 3:2 spin-orbit resonance: 58.6-day rotation, 88-day orbit).', 'ja': '1 太陽日 = 176 地球日（58.6 日の自転と 88 日公転の 3:2 共鳴）。' },
      { 'zh-Hant': '軌道離心率 0.21，近日點與遠日點距日相差 ~24%。', 'en': 'Orbital eccentricity 0.21 — perihelion vs aphelion distances differ by ~24%.', 'ja': '軌道離心率 0.21、近日点と遠日点で太陽距離が 24% 異なる。' },
    ],
  },

  venus: {
    classification: { 'zh-Hant': '類地行星（地球姊妹）', 'en': 'Terrestrial planet (Earth\'s sister)', 'ja': '地球型惑星（地球の姉妹星）' },
    meanSurfaceTempC: 464,
    surfaceTempRangeC: [437, 482],
    surfaceGravityMS2: 8.87,
    escapeVelocityKmS: 10.36,
    bulkComposition: [
      { name: { 'zh-Hant': '矽酸鹽地函', 'en': 'Silicate mantle', 'ja': 'ケイ酸塩マントル' }, pct: 70 },
      { name: { 'zh-Hant': '鐵核心', 'en': 'Iron core', 'ja': '鉄の核' }, pct: 30 },
    ],
    atmosphere: {
      surfacePressureKpa: 9200,
      pressureNote: { 'zh-Hant': '92 倍地球大氣壓', 'en': '92× Earth atmospheric pressure', 'ja': '地球大気圧の 92 倍' },
      composition: [
        { name: { 'zh-Hant': '二氧化碳 (CO₂)', 'en': 'Carbon Dioxide (CO₂)', 'ja': '二酸化炭素 (CO₂)' }, pct: 96.5 },
        { name: { 'zh-Hant': '氮 (N₂)', 'en': 'Nitrogen (N₂)', 'ja': '窒素 (N₂)' }, pct: 3.5 },
        { name: { 'zh-Hant': '硫酸雲、SO₂、Ar', 'en': 'Sulfuric acid clouds, SO₂, Ar', 'ja': '硫酸雲、SO₂、Ar' }, pct: 0.015 },
      ],
    },
    internalStructure: [
      { layer: { 'zh-Hant': '核心', 'en': 'Core', 'ja': '中心核' },
        description: { 'zh-Hant': '半徑約 3 200 km，鐵-鎳；無內生磁場（自轉太慢或核心已凝固）。', 'en': '~3,200 km radius, iron-nickel; no intrinsic magnetic field (rotation too slow, or core solidified).', 'ja': '半径約 3,200 km、鉄-ニッケル。固有磁場なし（自転が遅すぎる、または核が固化）。' } },
      { layer: { 'zh-Hant': '地函', 'en': 'Mantle', 'ja': 'マントル' },
        description: { 'zh-Hant': '矽酸鹽，類似地球但無板塊運動。', 'en': 'Silicates, similar to Earth but without plate tectonics.', 'ja': 'ケイ酸塩。地球に似るがプレート運動はない。' } },
      { layer: { 'zh-Hant': '地殼', 'en': 'Crust', 'ja': '地殻' },
        description: { 'zh-Hant': '厚 50 km 玄武岩；估計每 3-6 億年發生一次全球性火山重鋪。', 'en': '50 km thick basalt; thought to undergo global volcanic resurfacing every 300–600 Myr.', 'ja': '厚さ 50 km の玄武岩。3〜6 億年ごとに全球火山リサーフェシングが起きると推定。' } },
    ],
    geology: { 'zh-Hant': '90% 表面為火山平原；超過 1 600 座大型火山。完全乾涸（H₂O < 20 ppm）。CO₂ 溫室效應使表面比水星更熱。',
      'en': '90% of the surface is volcanic plains; over 1,600 major volcanoes. Completely dry (H₂O < 20 ppm). Runaway CO₂ greenhouse makes the surface hotter than Mercury\'s.',
      'ja': '表面の 90% は火山平原、1,600 を超える大型火山。完全に乾燥（H₂O < 20 ppm）。CO₂ の暴走温室効果で水星より高温。' },
    notableFacts: [
      { 'zh-Hant': '逆向自轉，自轉週期 243 日比公轉 225 日還長。', 'en': 'Retrograde rotation; the 243-day day is longer than its 225-day year.', 'ja': '逆行自転。自転周期 243 日は公転周期 225 日よりも長い。' },
      { 'zh-Hant': '硫酸雲頂端風速可達 360 km/h（超自轉現象）。', 'en': 'Sulfuric acid cloud-top winds reach 360 km/h ("super-rotation").', 'ja': '硫酸雲頂の風速は 360 km/h（スーパーローテーション現象）。' },
    ],
  },

  earth: {
    classification: { 'zh-Hant': '類地行星（唯一已知具生命）', 'en': 'Terrestrial planet (only known with life)', 'ja': '地球型惑星（唯一生命を持つ）' },
    meanSurfaceTempC: 15,
    surfaceTempRangeC: [-89.2, 56.7],
    surfaceGravityMS2: 9.807,
    escapeVelocityKmS: 11.186,
    bulkComposition: [
      { name: { 'zh-Hant': '鐵 (Fe)', 'en': 'Iron (Fe)', 'ja': '鉄 (Fe)' }, pct: 32.1 },
      { name: { 'zh-Hant': '氧 (O)', 'en': 'Oxygen (O)', 'ja': '酸素 (O)' }, pct: 30.1 },
      { name: { 'zh-Hant': '矽 (Si)', 'en': 'Silicon (Si)', 'ja': 'ケイ素 (Si)' }, pct: 15.1 },
      { name: { 'zh-Hant': '鎂 (Mg)', 'en': 'Magnesium (Mg)', 'ja': 'マグネシウム (Mg)' }, pct: 13.9 },
      { name: { 'zh-Hant': '硫 (S)', 'en': 'Sulfur (S)', 'ja': '硫黄 (S)' }, pct: 2.9 },
      { name: { 'zh-Hant': '其他', 'en': 'Others', 'ja': 'その他' }, pct: 5.9 },
    ],
    atmosphere: {
      surfacePressureKpa: 101.325,
      composition: [
        { name: { 'zh-Hant': '氮 (N₂)', 'en': 'Nitrogen (N₂)', 'ja': '窒素 (N₂)' }, pct: 78.084 },
        { name: { 'zh-Hant': '氧 (O₂)', 'en': 'Oxygen (O₂)', 'ja': '酸素 (O₂)' }, pct: 20.946 },
        { name: { 'zh-Hant': '氬 (Ar)', 'en': 'Argon (Ar)', 'ja': 'アルゴン (Ar)' }, pct: 0.934 },
        { name: { 'zh-Hant': '二氧化碳 (CO₂)', 'en': 'Carbon Dioxide (CO₂)', 'ja': '二酸化炭素 (CO₂)' }, pct: 0.042 },
        { name: { 'zh-Hant': '水汽 (變動)', 'en': 'Water vapor (variable)', 'ja': '水蒸気 (変動)' }, pct: 0.4 },
      ],
    },
    internalStructure: [
      { layer: { 'zh-Hant': '內核', 'en': 'Inner Core', 'ja': '内核' },
        description: { 'zh-Hant': '半徑 1 220 km，固態鐵-鎳，溫度 ~5400°C。', 'en': '1,220 km radius, solid iron-nickel, ~5400°C.', 'ja': '半径 1,220 km、固体の鉄-ニッケル、~5400°C。' } },
      { layer: { 'zh-Hant': '外核', 'en': 'Outer Core', 'ja': '外核' },
        description: { 'zh-Hant': '厚 2 260 km，液態鐵-鎳；對流產生地球磁場。', 'en': '2,260 km thick, liquid iron-nickel; convection drives the geomagnetic field.', 'ja': '厚さ 2,260 km、液体の鉄-ニッケル。対流が地磁気を生む。' } },
      { layer: { 'zh-Hant': '下地函', 'en': 'Lower Mantle', 'ja': '下部マントル' },
        description: { 'zh-Hant': '厚 2 200 km，固態矽酸鹽（鈣鈦礦結構）。', 'en': '2,200 km thick, solid silicates (perovskite structure).', 'ja': '厚さ 2,200 km、固体ケイ酸塩（ペロブスカイト構造）。' } },
      { layer: { 'zh-Hant': '上地函 / 軟流圈', 'en': 'Upper Mantle / Asthenosphere', 'ja': '上部マントル / アセノスフェア' },
        description: { 'zh-Hant': '厚 670 km，部分熔融，驅動板塊運動。', 'en': '670 km thick, partially molten, drives plate tectonics.', 'ja': '厚さ 670 km、部分的に溶融、プレート運動を駆動。' } },
      { layer: { 'zh-Hant': '地殼', 'en': 'Crust', 'ja': '地殻' },
        description: { 'zh-Hant': '海洋地殼 5–10 km、大陸地殼 30–70 km。', 'en': 'Oceanic crust 5–10 km; continental crust 30–70 km.', 'ja': '海洋地殻 5–10 km、大陸地殻 30–70 km。' } },
    ],
    geology: { 'zh-Hant': '唯一具有活躍板塊構造的行星。約 71% 表面為水覆蓋。磁場保護大氣不被太陽風剝離。生命已存在 ~38 億年並大幅改造大氣。',
      'en': 'Only planet with active plate tectonics. ~71% surface water-covered. Magnetic field shields the atmosphere from solar wind. Life has reshaped the atmosphere over ~3.8 Gyr.',
      'ja': '活発なプレートテクトニクスを持つ唯一の惑星。表面の約 71% は水。磁場が大気を太陽風から守る。約 38 億年の生命活動が大気を改造。' },
    notableFacts: [
      { 'zh-Hant': '軸傾角 23.44° 造成季節變化。', 'en': 'Axial tilt of 23.44° causes the seasons.', 'ja': '自転軸傾斜 23.44° が四季を生む。' },
      { 'zh-Hant': '月球潮汐使地球自轉每世紀變慢 1.7 毫秒。', 'en': 'Lunar tides slow Earth\'s rotation by 1.7 ms per century.', 'ja': '月の潮汐により自転が 1 世紀あたり 1.7 ms 遅くなる。' },
      { 'zh-Hant': '預估 50 億年後太陽紅巨星化將吞噬地球。', 'en': 'In ~5 Gyr the Sun\'s red-giant phase will likely engulf Earth.', 'ja': '約 50 億年後、太陽の赤色巨星化により地球は呑み込まれる見込み。' },
    ],
  },

  mars: {
    classification: { 'zh-Hant': '類地行星（紅色行星）', 'en': 'Terrestrial planet (Red Planet)', 'ja': '地球型惑星（赤い惑星）' },
    meanSurfaceTempC: -63,
    surfaceTempRangeC: [-143, 35],
    surfaceGravityMS2: 3.71,
    escapeVelocityKmS: 5.03,
    bulkComposition: [
      { name: { 'zh-Hant': '鐵 (Fe，含氧化鐵)', 'en': 'Iron (Fe, incl. oxides)', 'ja': '鉄 (Fe、酸化鉄を含む)' }, pct: 27 },
      { name: { 'zh-Hant': '矽酸鹽地函', 'en': 'Silicate mantle', 'ja': 'ケイ酸塩マントル' }, pct: 65 },
      { name: { 'zh-Hant': '硫 (S)', 'en': 'Sulfur (S)', 'ja': '硫黄 (S)' }, pct: 8 },
    ],
    atmosphere: {
      surfacePressureKpa: 0.636,
      pressureNote: { 'zh-Hant': '~0.6% 地球大氣壓', 'en': '~0.6% of Earth\'s atmospheric pressure', 'ja': '地球大気圧の約 0.6%' },
      composition: [
        { name: { 'zh-Hant': '二氧化碳 (CO₂)', 'en': 'Carbon Dioxide (CO₂)', 'ja': '二酸化炭素 (CO₂)' }, pct: 95.32 },
        { name: { 'zh-Hant': '氮 (N₂)', 'en': 'Nitrogen (N₂)', 'ja': '窒素 (N₂)' }, pct: 2.7 },
        { name: { 'zh-Hant': '氬 (Ar)', 'en': 'Argon (Ar)', 'ja': 'アルゴン (Ar)' }, pct: 1.6 },
        { name: { 'zh-Hant': '氧 (O₂)', 'en': 'Oxygen (O₂)', 'ja': '酸素 (O₂)' }, pct: 0.13 },
        { name: { 'zh-Hant': '一氧化碳、水汽', 'en': 'CO + water vapor', 'ja': '一酸化炭素・水蒸気' }, pct: 0.25 },
      ],
    },
    internalStructure: [
      { layer: { 'zh-Hant': '核心', 'en': 'Core', 'ja': '中心核' },
        description: { 'zh-Hant': '半徑 1 700 km，部分液態硫鐵；磁場早期消失。', 'en': '~1,700 km radius, partly liquid iron-sulfur; intrinsic magnetic field died billions of years ago.', 'ja': '半径 1,700 km、部分液体の硫化鉄。磁場は早期に消失。' } },
      { layer: { 'zh-Hant': '地函', 'en': 'Mantle', 'ja': 'マントル' },
        description: { 'zh-Hant': '矽酸鹽，富含鎂與鐵，較地球冷。', 'en': 'Silicates rich in Mg and Fe; cooler than Earth\'s.', 'ja': 'マグネシウム・鉄に富むケイ酸塩。地球より低温。' } },
      { layer: { 'zh-Hant': '地殼', 'en': 'Crust', 'ja': '地殻' },
        description: { 'zh-Hant': '南半球古老（44 億年）厚 ~80 km，北半球年輕薄 ~32 km。', 'en': 'Southern hemisphere is ancient (4.4 Gyr) and ~80 km thick; northern hemisphere is younger and ~32 km thick.', 'ja': '南半球は古く（44 億年）厚さ ~80 km、北半球は若く ~32 km。' } },
    ],
    geology: { 'zh-Hant': '奧林帕斯山高 25 km、底寬 600 km，太陽系最大火山。水手號峽谷長 4 000 km、深 7 km。極冠由 CO₂ 與水冰組成，季節性升降。表面紅色源於氧化鐵（Fe₂O₃）。',
      'en': 'Olympus Mons is 25 km tall and 600 km wide — the largest volcano in the Solar System. Valles Marineris is 4,000 km long and 7 km deep. Polar caps are CO₂ + water ice that grow and shrink with the seasons. Surface red colour comes from iron oxide (Fe₂O₃).',
      'ja': 'オリンポス山は高さ 25 km・直径 600 km で太陽系最大の火山。マリネリス峡谷は長さ 4,000 km、深さ 7 km。極冠は CO₂ と水氷からなり季節変動。赤色は酸化鉄 (Fe₂O₃) に由来。' },
    notableFacts: [
      { 'zh-Hant': '一個火星年約 687 地球日。', 'en': 'A Martian year is ~687 Earth days.', 'ja': '1 火星年は約 687 地球日。' },
      { 'zh-Hant': '兩顆衛星 Phobos、Deimos 可能是被捕獲的小行星。', 'en': 'The two moons Phobos and Deimos may be captured asteroids.', 'ja': '2 衛星フォボスとダイモスは捕獲小惑星と推定される。' },
      { 'zh-Hant': 'NASA Insight 偵測到火星震，證明內部仍有地質活動。', 'en': 'NASA InSight detected marsquakes, confirming the interior is still geologically active.', 'ja': 'NASA InSight が火星地震を検出し、内部の地質活動を確認。' },
    ],
  },

  jupiter: {
    classification: { 'zh-Hant': '氣態巨行星（最大行星）', 'en': 'Gas giant (largest planet)', 'ja': '巨大ガス惑星（最大の惑星）' },
    meanSurfaceTempC: -145,
    surfaceTempRangeC: [-145, -110],
    surfaceGravityMS2: 24.79,
    escapeVelocityKmS: 59.5,
    bulkComposition: [
      { name: { 'zh-Hant': '氫 (H₂)', 'en': 'Hydrogen (H₂)', 'ja': '水素 (H₂)' }, pct: 89 },
      { name: { 'zh-Hant': '氦 (He)', 'en': 'Helium (He)', 'ja': 'ヘリウム (He)' }, pct: 10 },
      { name: { 'zh-Hant': '甲烷、氨、水汽', 'en': 'Methane, ammonia, water vapor', 'ja': 'メタン・アンモニア・水蒸気' }, pct: 1 },
    ],
    atmosphere: {
      pressureNote: { 'zh-Hant': '無固體表面；分層：對流層→平流層→熱層', 'en': 'No solid surface; layered troposphere → stratosphere → thermosphere.', 'ja': '固体表面なし。対流圏→成層圏→熱圏の層構造。' },
      composition: [
        { name: { 'zh-Hant': '氫 (H₂)', 'en': 'Hydrogen (H₂)', 'ja': '水素 (H₂)' }, pct: 89.8 },
        { name: { 'zh-Hant': '氦 (He)', 'en': 'Helium (He)', 'ja': 'ヘリウム (He)' }, pct: 10.2 },
        { name: { 'zh-Hant': '甲烷 (CH₄)', 'en': 'Methane (CH₄)', 'ja': 'メタン (CH₄)' }, pct: 0.3 },
        { name: { 'zh-Hant': '氨 (NH₃)', 'en': 'Ammonia (NH₃)', 'ja': 'アンモニア (NH₃)' }, pct: 0.026 },
        { name: { 'zh-Hant': '水汽 (H₂O)', 'en': 'Water vapor (H₂O)', 'ja': '水蒸気 (H₂O)' }, pct: 0.0004 },
      ],
    },
    internalStructure: [
      { layer: { 'zh-Hant': '雲層大氣', 'en': 'Cloud-deck atmosphere', 'ja': '雲層大気' },
        description: { 'zh-Hant': '頂部 0–80 km：氨冰雲、氫硫化銨雲、水冰雲三層。', 'en': 'Top 0–80 km: three cloud decks of ammonia-ice, ammonium-hydrosulfide, and water-ice.', 'ja': '上部 0–80 km：アンモニア氷雲、硫化アンモニウム雲、水氷雲の 3 層構造。' } },
      { layer: { 'zh-Hant': '分子氫層', 'en': 'Molecular hydrogen layer', 'ja': '分子水素層' },
        description: { 'zh-Hant': '從雲層底部至 20 000 km 深，壓力到 100 萬 atm。', 'en': 'From cloud-base down to 20,000 km depth; pressures up to 1 million atm.', 'ja': '雲底から深さ 20,000 km まで。圧力は 100 万気圧に到達。' } },
      { layer: { 'zh-Hant': '金屬氫層', 'en': 'Metallic hydrogen layer', 'ja': '金属水素層' },
        description: { 'zh-Hant': '20 000–55 000 km；液態金屬氫導電產生強大磁場。', 'en': '20,000–55,000 km depth; liquid metallic hydrogen conducts electricity, generating Jupiter\'s strong magnetic field.', 'ja': '深さ 20,000–55,000 km。液体金属水素が導電性を持ち強磁場を生成。' } },
      { layer: { 'zh-Hant': '核心', 'en': 'Core', 'ja': '中心核' },
        description: { 'zh-Hant': '可能為「稀釋核心」(diluted core)，富岩石與冰，質量 ~10 地球質量。', 'en': 'Likely a "diluted core" rich in rock and ices, ~10 Earth masses.', 'ja': '「希薄化した核」と推定され、岩石と氷に富む。質量は地球の約 10 倍。' } },
    ],
    geology: { 'zh-Hant': '無固體表面。著名「大紅斑」是反氣旋風暴，已存在至少 350 年。磁場強度為地球 14 倍，磁層延伸至土星軌道。',
      'en': 'No solid surface. The famous Great Red Spot is an anticyclonic storm that has persisted for at least 350 years. Magnetic field strength is 14× Earth\'s; the magnetosphere extends out to Saturn\'s orbit.',
      'ja': '固体表面なし。有名な「大赤斑」は 350 年以上続く高気圧性嵐。磁場強度は地球の 14 倍で、磁気圏は土星軌道まで及ぶ。' },
    notableFacts: [
      { 'zh-Hant': '主要行星帶有 95 顆已知衛星（伽利略四大衛星最為著名）。', 'en': '95 known moons (the four Galilean moons are best known).', 'ja': '95 個の既知衛星（ガリレオ衛星 4 個が著名）。' },
      { 'zh-Hant': '一日約 9 小時 56 分，自轉最快的行星，赤道離心率 6.5%。', 'en': 'A 9 h 56 min day — fastest rotation of any planet; equatorial bulge of 6.5%.', 'ja': '1 日は約 9 時間 56 分で全惑星中最速の自転。赤道膨らみ 6.5%。' },
      { 'zh-Hant': '若質量再大 80 倍即可成為棕矮星。', 'en': 'About 80× more mass and it would have ignited as a brown dwarf.', 'ja': '質量があと 80 倍あれば褐色矮星になっていた。' },
    ],
  },

  saturn: {
    classification: { 'zh-Hant': '氣態巨行星（環系最壯麗）', 'en': 'Gas giant (most spectacular ring system)', 'ja': '巨大ガス惑星（最も壮大な環系）' },
    meanSurfaceTempC: -178,
    surfaceTempRangeC: [-189, -139],
    surfaceGravityMS2: 10.44,
    escapeVelocityKmS: 35.5,
    bulkComposition: [
      { name: { 'zh-Hant': '氫 (H₂)', 'en': 'Hydrogen (H₂)', 'ja': '水素 (H₂)' }, pct: 96.3 },
      { name: { 'zh-Hant': '氦 (He)', 'en': 'Helium (He)', 'ja': 'ヘリウム (He)' }, pct: 3.25 },
      { name: { 'zh-Hant': '甲烷、氨', 'en': 'Methane, ammonia', 'ja': 'メタン・アンモニア' }, pct: 0.45 },
    ],
    atmosphere: {
      pressureNote: { 'zh-Hant': '無固體表面；雲頂風速可達 1 800 km/h', 'en': 'No solid surface; cloud-top winds reach 1,800 km/h.', 'ja': '固体表面なし。雲頂風速は 1,800 km/h に達する。' },
      composition: [
        { name: { 'zh-Hant': '氫 (H₂)', 'en': 'Hydrogen (H₂)', 'ja': '水素 (H₂)' }, pct: 96.3 },
        { name: { 'zh-Hant': '氦 (He)', 'en': 'Helium (He)', 'ja': 'ヘリウム (He)' }, pct: 3.25 },
        { name: { 'zh-Hant': '甲烷 (CH₄)', 'en': 'Methane (CH₄)', 'ja': 'メタン (CH₄)' }, pct: 0.45 },
        { name: { 'zh-Hant': '氨 (NH₃)', 'en': 'Ammonia (NH₃)', 'ja': 'アンモニア (NH₃)' }, pct: 0.0125 },
      ],
    },
    internalStructure: [
      { layer: { 'zh-Hant': '分子氫層', 'en': 'Molecular hydrogen layer', 'ja': '分子水素層' },
        description: { 'zh-Hant': '雲層至 30 000 km 深處。', 'en': 'From the cloud deck down to 30,000 km depth.', 'ja': '雲層から深さ 30,000 km まで。' } },
      { layer: { 'zh-Hant': '金屬氫層', 'en': 'Metallic hydrogen layer', 'ja': '金属水素層' },
        description: { 'zh-Hant': '較木星淺薄。', 'en': 'Thinner than Jupiter\'s.', 'ja': '木星より薄い。' } },
      { layer: { 'zh-Hant': '核心', 'en': 'Core', 'ja': '中心核' },
        description: { 'zh-Hant': '岩石+冰，質量約 9–22 地球質量；最新分析認為可能為延伸至 60% 半徑的稀釋核心。', 'en': 'Rock + ice, ~9–22 Earth masses. Recent analyses suggest a diluted core that may extend out to 60% of the planet\'s radius.', 'ja': '岩石＋氷、質量は地球の 9–22 倍。近年の解析では惑星半径の 60% まで広がる希薄核の可能性。' } },
    ],
    geology: { 'zh-Hant': '密度 0.687 g/cm³，是太陽系唯一密度小於水的行星。北極有六邊形噴流，邊長 14 500 km。環系由 99% 水冰粒子構成，厚度僅 10 m–1 km，寬卻達 28 萬 km。',
      'en': 'Density 0.687 g/cm³ — the only planet less dense than water. A hexagonal jet stream sits at the north pole, with sides 14,500 km long. The ring system is 99% water-ice particles, only 10 m – 1 km thick but 280,000 km wide.',
      'ja': '密度 0.687 g/cm³ で、水より低密度な唯一の惑星。北極には辺 14,500 km の六角形ジェット気流。環系は 99% 水氷粒子、厚さ 10 m〜1 km、幅 28 万 km。' },
    notableFacts: [
      { 'zh-Hant': '146 顆已知衛星，土衛六（泰坦）有濃厚大氣與液態甲烷湖泊。', 'en': '146 known moons; Titan has a thick atmosphere and lakes of liquid methane.', 'ja': '146 個の既知衛星。タイタンは濃厚な大気と液体メタンの湖を持つ。' },
      { 'zh-Hant': '土衛二（恩克拉多斯）冰殼下有液態海洋，南極噴泉噴出水蒸氣。', 'en': 'Enceladus hides a subsurface liquid ocean; geysers at its south pole erupt water vapor.', 'ja': 'エンケラドゥスは氷殻下に液体の海を持ち、南極から水蒸気を噴出。' },
    ],
  },

  uranus: {
    classification: { 'zh-Hant': '冰巨行星', 'en': 'Ice giant', 'ja': '巨大氷惑星' },
    meanSurfaceTempC: -224,
    surfaceTempRangeC: [-224, -216],
    surfaceGravityMS2: 8.87,
    escapeVelocityKmS: 21.3,
    bulkComposition: [
      { name: { 'zh-Hant': '冰類（H₂O、NH₃、CH₄）', 'en': 'Ices (H₂O, NH₃, CH₄)', 'ja': '氷類 (H₂O, NH₃, CH₄)' }, pct: 80 },
      { name: { 'zh-Hant': '氫氦氣體外殼', 'en': 'Hydrogen-helium gaseous envelope', 'ja': '水素-ヘリウム外気殻' }, pct: 15 },
      { name: { 'zh-Hant': '岩石核心', 'en': 'Rocky core', 'ja': '岩石の核' }, pct: 5 },
    ],
    atmosphere: {
      pressureNote: { 'zh-Hant': '無固體表面；雲頂溫度為太陽系行星最冷', 'en': 'No solid surface; coldest cloud-top temperatures of any Solar-System planet.', 'ja': '固体表面なし。雲頂温度は太陽系の惑星で最も低い。' },
      composition: [
        { name: { 'zh-Hant': '氫 (H₂)', 'en': 'Hydrogen (H₂)', 'ja': '水素 (H₂)' }, pct: 82.5 },
        { name: { 'zh-Hant': '氦 (He)', 'en': 'Helium (He)', 'ja': 'ヘリウム (He)' }, pct: 15.2 },
        { name: { 'zh-Hant': '甲烷 (CH₄)', 'en': 'Methane (CH₄)', 'ja': 'メタン (CH₄)' }, pct: 2.3 },
      ],
    },
    internalStructure: [
      { layer: { 'zh-Hant': '大氣', 'en': 'Atmosphere', 'ja': '大気' },
        description: { 'zh-Hant': '氫-氦混合氣體，含甲烷使其呈青藍色。', 'en': 'Hydrogen-helium mix; methane gives it the cyan-blue colour.', 'ja': '水素-ヘリウム混合気体。メタンが青緑色を生む。' } },
      { layer: { 'zh-Hant': '冰幔', 'en': 'Icy mantle', 'ja': '氷マントル' },
        description: { 'zh-Hant': '主要為「熱液態」水、氨、甲烷混合（不是固態冰）；可能存在電離水的怪異物相。', 'en': 'Mostly "hot fluid" water, ammonia, and methane (not solid ice); may host exotic ionised-water phases.', 'ja': '高温流体の水・アンモニア・メタン混合（固体氷ではない）。電離水のエキゾチック相が存在する可能性。' } },
      { layer: { 'zh-Hant': '岩石核心', 'en': 'Rocky core', 'ja': '岩石の核' },
        description: { 'zh-Hant': '矽酸鹽+鐵，質量約 0.55 地球質量。', 'en': 'Silicates + iron, ~0.55 Earth masses.', 'ja': 'ケイ酸塩+鉄、質量は地球の約 0.55 倍。' } },
    ],
    geology: { 'zh-Hant': '自轉軸傾角 97.77°，幾乎側躺繞日；推測曾被地球大小天體撞擊。每 84 年完成一個軌道，每極連續 42 年日照／42 年黑暗。',
      'en': 'Axial tilt 97.77° — orbits nearly on its side, possibly because of an Earth-sized impactor. 84-year orbit means each pole sees 42 years of continuous daylight followed by 42 years of darkness.',
      'ja': '自転軸傾斜 97.77°、ほぼ横倒しで公転（地球サイズ天体との衝突が原因と推定）。84 年周期の公転で、両極とも 42 年連続日照と 42 年連続夜が交互に続く。' },
    notableFacts: [
      { 'zh-Hant': '27 顆已知衛星，多以莎士比亞與蒲柏的角色命名。', 'en': '27 known moons, mostly named after Shakespeare and Pope characters.', 'ja': '27 個の既知衛星、多くはシェイクスピアやポープの登場人物に因む。' },
      { 'zh-Hant': '13 條暗淡環，由 1977 年掩星觀測首次發現。', 'en': '13 dim rings, first discovered during a 1977 stellar occultation.', 'ja': '13 本の暗い環。1977 年の恒星掩蔽観測で初めて発見。' },
    ],
  },

  neptune: {
    classification: { 'zh-Hant': '冰巨行星', 'en': 'Ice giant', 'ja': '巨大氷惑星' },
    meanSurfaceTempC: -218,
    surfaceTempRangeC: [-218, -200],
    surfaceGravityMS2: 11.15,
    escapeVelocityKmS: 23.5,
    bulkComposition: [
      { name: { 'zh-Hant': '冰類', 'en': 'Ices', 'ja': '氷類' }, pct: 80 },
      { name: { 'zh-Hant': '氫氦', 'en': 'Hydrogen-helium', 'ja': '水素-ヘリウム' }, pct: 15 },
      { name: { 'zh-Hant': '岩石', 'en': 'Rock', 'ja': '岩石' }, pct: 5 },
    ],
    atmosphere: {
      pressureNote: { 'zh-Hant': '無固體表面；風速可達 2 100 km/h（太陽系最快）', 'en': 'No solid surface; winds up to 2,100 km/h (fastest in the Solar System).', 'ja': '固体表面なし。風速は 2,100 km/h（太陽系最速）に達する。' },
      composition: [
        { name: { 'zh-Hant': '氫 (H₂)', 'en': 'Hydrogen (H₂)', 'ja': '水素 (H₂)' }, pct: 80 },
        { name: { 'zh-Hant': '氦 (He)', 'en': 'Helium (He)', 'ja': 'ヘリウム (He)' }, pct: 19 },
        { name: { 'zh-Hant': '甲烷 (CH₄)', 'en': 'Methane (CH₄)', 'ja': 'メタン (CH₄)' }, pct: 1.5 },
      ],
    },
    internalStructure: [
      { layer: { 'zh-Hant': '大氣', 'en': 'Atmosphere', 'ja': '大気' },
        description: { 'zh-Hant': '氫-氦含甲烷；深藍色源於甲烷吸收紅光。', 'en': 'Hydrogen-helium with methane; the deep-blue colour comes from methane absorbing red light.', 'ja': '水素-ヘリウム＋メタン。深い青はメタンが赤い光を吸収するため。' } },
      { layer: { 'zh-Hant': '冰幔', 'en': 'Icy mantle', 'ja': '氷マントル' },
        description: { 'zh-Hant': '溫熱液態水/氨/甲烷混合，內部熱量 2.6× 接收的太陽能。', 'en': 'Hot liquid water/ammonia/methane mix; internal heat is 2.6× the solar input.', 'ja': '高温の水・アンモニア・メタン液体混合。内部熱量は受光太陽エネルギーの 2.6 倍。' } },
      { layer: { 'zh-Hant': '岩石核心', 'en': 'Rocky core', 'ja': '岩石の核' },
        description: { 'zh-Hant': '質量約 1.2 地球質量。', 'en': '~1.2 Earth masses.', 'ja': '質量は地球の約 1.2 倍。' } },
    ],
    geology: { 'zh-Hant': '內部熱源使其風速比天王星更狂暴。大暗斑（Voyager 2 觀察）為週期性反氣旋風暴。海王星發現於 1846 年，先以萬有引力推算位置再被觀測——數學發現的行星。',
      'en': 'Internal heat drives winds more violent than Uranus\'. The Great Dark Spot (observed by Voyager 2) is a recurring anticyclonic storm. Discovered in 1846 — first predicted by gravitational calculation, then observed: a planet found mathematically.',
      'ja': '内部熱源が天王星よりも激しい風を生む。大暗斑（ボイジャー 2 号が観測）は周期的な高気圧性嵐。1846 年、万有引力の計算で位置を予測し観測された「数学的に発見された惑星」。' },
    notableFacts: [
      { 'zh-Hant': '14 顆衛星；衛一（崔頓）逆向公轉，可能是被捕獲的古柏帶天體。', 'en': '14 moons; Triton orbits retrograde and may be a captured Kuiper-belt object.', 'ja': '14 個の衛星。トリトンは逆行公転で、捕獲されたカイパーベルト天体の可能性。' },
      { 'zh-Hant': '一個海王星年 = 165 地球年；自 1846 年發現以來只完成了一個公轉週期（2011 年）。', 'en': 'A Neptunian year is 165 Earth years; since its 1846 discovery, only one full orbit has elapsed (in 2011).', 'ja': '1 海王星年は 165 地球年。1846 年の発見以来、公転を 1 周完了したのは 2011 年。' },
    ],
  },

  moon: {
    classification: { 'zh-Hant': '地球的天然衛星', 'en': 'Earth\'s natural satellite', 'ja': '地球の天然衛星' },
    meanSurfaceTempC: -23,
    surfaceTempRangeC: [-173, 127],
    surfaceGravityMS2: 1.62,
    escapeVelocityKmS: 2.38,
    bulkComposition: [
      { name: { 'zh-Hant': '氧 (O)', 'en': 'Oxygen (O)', 'ja': '酸素 (O)' }, pct: 43 },
      { name: { 'zh-Hant': '矽 (Si)', 'en': 'Silicon (Si)', 'ja': 'ケイ素 (Si)' }, pct: 21 },
      { name: { 'zh-Hant': '鎂 (Mg)', 'en': 'Magnesium (Mg)', 'ja': 'マグネシウム (Mg)' }, pct: 19 },
      { name: { 'zh-Hant': '鐵 (Fe)', 'en': 'Iron (Fe)', 'ja': '鉄 (Fe)' }, pct: 10 },
      { name: { 'zh-Hant': '鈣、鋁、其他', 'en': 'Calcium, aluminium, others', 'ja': 'カルシウム・アルミ・その他' }, pct: 7 },
    ],
    atmosphere: {
      pressureNote: { 'zh-Hant': '近乎真空（外逸層粒子數 ~10⁵/cm³）', 'en': 'Essentially vacuum (exosphere ~10⁵ particles/cm³)', 'ja': 'ほぼ真空（外圏粒子密度 ~10⁵/cm³）' },
      composition: [
        { name: { 'zh-Hant': '氦 (He)', 'en': 'Helium (He)', 'ja': 'ヘリウム (He)' }, pct: 25 },
        { name: { 'zh-Hant': '氖 (Ne)', 'en': 'Neon (Ne)', 'ja': 'ネオン (Ne)' }, pct: 25 },
        { name: { 'zh-Hant': '氫 (H)', 'en': 'Hydrogen (H)', 'ja': '水素 (H)' }, pct: 23 },
        { name: { 'zh-Hant': '氬 (Ar)', 'en': 'Argon (Ar)', 'ja': 'アルゴン (Ar)' }, pct: 20 },
        { name: { 'zh-Hant': '其他', 'en': 'Others', 'ja': 'その他' }, pct: 7 },
      ],
    },
    internalStructure: [
      { layer: { 'zh-Hant': '內核', 'en': 'Inner Core', 'ja': '内核' },
        description: { 'zh-Hant': '固態鐵，半徑 ~240 km。', 'en': 'Solid iron, ~240 km radius.', 'ja': '固体の鉄、半径 ~240 km。' } },
      { layer: { 'zh-Hant': '外核', 'en': 'Outer Core', 'ja': '外核' },
        description: { 'zh-Hant': '液態鐵，厚 ~90 km；磁場早已消失。', 'en': 'Liquid iron, ~90 km thick; intrinsic magnetic field died long ago.', 'ja': '液体鉄、厚さ ~90 km。固有磁場ははるか昔に消失。' } },
      { layer: { 'zh-Hant': '部分熔融層', 'en': 'Partial-melt layer', 'ja': '部分溶融層' },
        description: { 'zh-Hant': '厚 ~150 km，月震多源於此。', 'en': '~150 km thick; the source of most moonquakes.', 'ja': '厚さ ~150 km。月震の主な震源。' } },
      { layer: { 'zh-Hant': '地函', 'en': 'Mantle', 'ja': 'マントル' },
        description: { 'zh-Hant': '矽酸鹽，富鎂鐵礦物。', 'en': 'Silicates rich in Mg-Fe minerals.', 'ja': 'マグネシウム・鉄に富むケイ酸塩。' } },
      { layer: { 'zh-Hant': '月殼', 'en': 'Lunar crust', 'ja': '月殻' },
        description: { 'zh-Hant': '正面 ~50 km、背面 ~150 km；不對稱原因尚有爭議。', 'en': '~50 km on the near side, ~150 km on the far side; the asymmetry is still debated.', 'ja': '表側 ~50 km、裏側 ~150 km。非対称性の原因は未解明。' } },
    ],
    geology: { 'zh-Hant': '主流形成假說「大撞擊」：早期地球與火星大小天體碰撞，碎片凝聚成月球。表面分為亮高地（斜長岩）與暗月海（玄武岩流）。沒有大氣與水侵蝕，撞擊坑保存完好。',
      'en': 'Leading formation hypothesis is the giant-impact theory: a Mars-sized body struck the early Earth, with debris coalescing into the Moon. Surface splits into bright highlands (anorthosite) and dark maria (basaltic flows). With no atmosphere or water erosion, craters are preserved indefinitely.',
      'ja': '主流の形成仮説は「ジャイアントインパクト」：原始地球に火星サイズの天体が衝突し、その破片から月が形成。表面は明るい高地（斜長岩）と暗い海（玄武岩流）に分かれる。大気も水浸食もないため、クレーターが永久に保存される。' },
    notableFacts: [
      { 'zh-Hant': '同步自轉：永遠以同一面（正面）對著地球。', 'en': 'Tidally locked: always shows the same near-side hemisphere to Earth.', 'ja': '同期自転：常に同じ面（表側）を地球に向ける。' },
      { 'zh-Hant': '每年遠離地球 3.8 cm。', 'en': 'Recedes from Earth by 3.8 cm per year.', 'ja': '毎年 3.8 cm ずつ地球から遠ざかっている。' },
      { 'zh-Hant': '導致地球潮汐並穩定地球自轉軸（沒有月球，地球軸傾角會混沌變化）。', 'en': 'Drives Earth\'s tides and stabilises its rotation axis — without the Moon, Earth\'s tilt would vary chaotically.', 'ja': '地球の潮汐を生み、自転軸を安定させる。月がなければ自転軸傾斜は混沌的に変動する。' },
    ],
  },
};
